import {
  Alert, Box, Button, Modal, Select, SpaceBetween, Spinner, Toggle,
} from '@cloudscape-design/components';
import type { SelectProps } from '@cloudscape-design/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from './apiFetch';
import { getAwsCreds } from './awsCredentials';

/**
 * EcsLogsModal — streams CloudWatch logs for an ECS service's active task definition.
 *
 * Log display uses a monospace, auto-scrolling <Box> fallback: @cloudscape-design/code-view is
 * NOT installed in this project and adding a new npm dependency is out of scope for this
 * feature (checked package.json + node_modules — absent).
 *
 * Polling model (Q6/Q14): the initial fetch loads a time window (range select: 15m/1h/6h,
 * default 1h). A Live toggle then polls every 4s using the server's forward `nextToken` to
 * fetch only newer events. A ~1000-line rolling buffer drops the oldest lines. Auto-scrolls to
 * the bottom whenever new lines arrive.
 */

interface EcsServiceOption {
  documentId: string;
  serviceName: string;
  containers?: { name: string }[];
}

interface EcsLogsModalProps {
  visible: boolean;
  onDismiss: () => void;
  ecsServices: EcsServiceOption[];
  accountAlias: string | null;
  region: string | null;
}

interface LogEvent {
  timestamp: number;
  message: string;
}

const RANGE_OPTIONS: SelectProps.Option[] = [
  { label: 'Last 15 minutes', value: '900' },
  { label: 'Last 1 hour', value: '3600' },
  { label: 'Last 6 hours', value: '21600' },
];

const MAX_LINES = 1000;
const POLL_INTERVAL_MS = 4000;
const NOT_SYNCED_CODE = 'ECS_LOGS_NOT_SYNCED';

export default function EcsLogsModal({
  visible, onDismiss, ecsServices, accountAlias, region,
}: EcsLogsModalProps) {
  const [selectedService, setSelectedService] = useState<EcsServiceOption | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [range, setRange] = useState<SelectProps.Option>(RANGE_OPTIONS[1]);

  const [events, setEvents] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notSynced, setNotSynced] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [live, setLive] = useState(false);
  const [noCreds, setNoCreds] = useState(false);

  const nextTokenRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logEndRef = useRef<HTMLDivElement | null>(null);
  const containers = selectedService?.containers ?? [];

  // Auto-select single service / reset on open.
  useEffect(() => {
    if (!visible) return;
    setSelectedService(ecsServices.length >= 1 ? ecsServices[0] : null);
    setEvents([]);
    setError(null);
    setNotSynced(false);
    setLive(false);
    nextTokenRef.current = null;
  }, [visible, ecsServices]);

  // Auto-select single container when service changes.
  useEffect(() => {
    const cs = selectedService?.containers ?? [];
    setSelectedContainer(cs.length >= 1 ? cs[0].name : null);
  }, [selectedService]);

  const buildCredHeaders = useCallback((): Record<string, string> | null => {
    if (!accountAlias) return null;
    const creds = getAwsCreds(accountAlias);
    if (!creds) return null;
    return {
      'X-AWS-Access-Key-Id': creds.accessKeyId,
      'X-AWS-Secret-Access-Key': creds.secretAccessKey,
      'X-AWS-Session-Token': creds.sessionToken,
    };
  }, [accountAlias]);

  // Core fetch. `incremental` uses the stored nextToken to append only newer events (polling);
  // a non-incremental call loads the selected time range fresh.
  const fetchLogs = useCallback(async (incremental: boolean) => {
    if (!selectedService) return;
    const headers = buildCredHeaders();
    if (!headers) { setNoCreds(true); return; }
    setNoCreds(false);

    if (!incremental) { setLoading(true); setError(null); setNotSynced(false); }

    const params = new URLSearchParams();
    if (selectedContainer) params.set('container', selectedContainer);
    if (incremental && nextTokenRef.current) {
      params.set('nextToken', nextTokenRef.current);
    } else {
      params.set('rangeSeconds', String(range.value));
    }

    try {
      const res = await apiFetch(
        `/ecs-services/${selectedService.documentId}/logs?${params.toString()}`,
        { headers },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const code = body?.error?.details?.code;
        if (res.status === 400 && code === NOT_SYNCED_CODE) {
          setNotSynced(true);
          setLive(false);
          return;
        }
        throw new Error(body?.error?.message || `Failed to fetch logs: ${res.status}`);
      }
      const data = await res.json();
      nextTokenRef.current = data.nextToken ?? nextTokenRef.current;
      const newEvents: LogEvent[] = data.events ?? [];
      if (incremental) {
        if (newEvents.length > 0) {
          setEvents((prev) => [...prev, ...newEvents].slice(-MAX_LINES));
        }
      } else {
        setEvents(newEvents.slice(-MAX_LINES));
      }
    } catch (e: any) {
      setError(e.message);
      setLive(false);
    } finally {
      if (!incremental) setLoading(false);
    }
  }, [selectedService, selectedContainer, range, buildCredHeaders]);

  // Initial / range / container / service change -> fresh load (when modal open).
  useEffect(() => {
    if (!visible || !selectedService) return;
    nextTokenRef.current = null;
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedService, selectedContainer, range]);

  // Live polling.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (live && visible && !notSynced) {
      pollRef.current = setInterval(() => fetchLogs(true), POLL_INTERVAL_MS);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [live, visible, notSynced, fetchLogs]);

  // Stop polling when modal closes.
  useEffect(() => {
    if (!visible && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [visible]);

  // Auto-scroll to bottom on new events.
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [events]);

  const handleResync = async () => {
    if (!selectedService) return;
    const headers = buildCredHeaders();
    if (!headers) { setNoCreds(true); return; }
    setResyncing(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/inventory/ecs-service/${selectedService.documentId}/resync`,
        { method: 'POST', headers },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || `Re-sync failed: ${res.status}`);
      }
      // Re-sync succeeded — retry the log fetch (metadata should now be present).
      setNotSynced(false);
      nextTokenRef.current = null;
      await fetchLogs(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setResyncing(false);
    }
  };

  const logText = events
    .map((e) => `${new Date(e.timestamp).toISOString()}  ${e.message}`)
    .join('\n');

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      header="ECS Logs"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>Close</Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <SpaceBetween direction="horizontal" size="m">
          {ecsServices.length > 1 && (
            <Select
              selectedOption={selectedService
                ? { label: selectedService.serviceName, value: selectedService.documentId }
                : null}
              onChange={({ detail }) => {
                const svc = ecsServices.find((s) => s.documentId === detail.selectedOption.value) ?? null;
                setSelectedService(svc);
              }}
              options={ecsServices.map((s) => ({ label: s.serviceName, value: s.documentId }))}
              placeholder="Select service"
            />
          )}
          {containers.length > 1 && (
            <Select
              selectedOption={selectedContainer
                ? { label: selectedContainer, value: selectedContainer }
                : null}
              onChange={({ detail }) => setSelectedContainer(detail.selectedOption.value ?? null)}
              options={containers.map((c) => ({ label: c.name, value: c.name }))}
              placeholder="Select container"
            />
          )}
          <Select
            selectedOption={range}
            onChange={({ detail }) => setRange(detail.selectedOption)}
            options={RANGE_OPTIONS}
            disabled={live}
          />
          <Toggle checked={live} onChange={({ detail }) => setLive(detail.checked)} disabled={notSynced}>
            Live
          </Toggle>
        </SpaceBetween>

        {noCreds && (
          <Alert type="warning" header="AWS credentials required">
            No AWS credentials configured for account '{accountAlias ?? 'unknown'}'. Go to Settings to set them up.
          </Alert>
        )}

        {notSynced ? (
          <Alert
            type="warning"
            header="Log metadata not synced"
            action={<Button loading={resyncing} onClick={handleResync}>Re-sync now</Button>}
          >
            This ECS service has no cached CloudWatch log configuration. Re-sync the service to
            fetch its task definition log metadata, then logs will load automatically.
          </Alert>
        ) : error ? (
          <Alert type="error">{error}</Alert>
        ) : loading ? (
          <Spinner size="large" />
        ) : (
          <Box
            variant="code"
            fontSize="body-s"
          >
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '60vh',
                overflowY: 'auto',
                fontFamily: 'monospace',
              }}
            >
              {logText || 'No log events in the selected range.'}
              <div ref={logEndRef} />
            </div>
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
}
